/**
 * React hooks for Test Schedules API
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { testSchedulesApi, TestScheduleCreate, TestScheduleUpdate, TestType } from '../api/test-schedules';
import { toast } from 'sonner';

/**
 * Get test schedules created by current therapist
 */
export function useMyTestSchedules(params?: {
  is_active?: boolean;
  test_type?: TestType;
  patient_id?: number;
}) {
  return useQuery({
    queryKey: ['test-schedules', 'therapist', 'me', params],
    queryFn: () => testSchedulesApi.getMySchedules(params),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Get test schedules for a specific patient (Therapist view)
 */
export function usePatientTestSchedules(
  patientId: number,
  params?: {
    is_active?: boolean;
    test_type?: TestType;
  }
) {
  return useQuery({
    queryKey: ['test-schedules', 'patient', patientId, params],
    queryFn: () => testSchedulesApi.getPatientSchedules(patientId, params),
    enabled: !!patientId && patientId > 0,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Get my scheduled tests (Patient view)
 */
export function useMyScheduledTests(params?: {
  is_active?: boolean;
  test_type?: TestType;
}) {
  return useQuery({
    queryKey: ['test-schedules', 'patient', 'me', params],
    queryFn: () => testSchedulesApi.getMyScheduledTests(params),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Create a new test schedule
 */
export function useCreateTestSchedule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: TestScheduleCreate) => testSchedulesApi.createSchedule(data),
    onSuccess: (response) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['test-schedules', 'therapist', 'me'] });
      queryClient.invalidateQueries({ queryKey: ['test-schedules', 'patient', response.patient_id] });
      toast.success('Test scheduled successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to schedule test');
    },
  });
}

/**
 * Update a test schedule
 */
export function useUpdateTestSchedule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ scheduleId, data }: { scheduleId: number; data: TestScheduleUpdate }) =>
      testSchedulesApi.updateSchedule(scheduleId, data),
    onSuccess: () => {
      // Invalidate all test schedule queries
      queryClient.invalidateQueries({ queryKey: ['test-schedules'] });
      toast.success('Test schedule updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update test schedule');
    },
  });
}

/**
 * Delete a test schedule
 */
export function useDeleteTestSchedule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (scheduleId: number) => testSchedulesApi.deleteSchedule(scheduleId),
    onSuccess: () => {
      // Invalidate all test schedule queries
      queryClient.invalidateQueries({ queryKey: ['test-schedules'] });
      toast.success('Test schedule deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to delete test schedule');
    },
  });
}
